/*
<Field /> is a helper component to setup a common Form Field look and feel.

  It expects to be provided a child content node that is the
  actual input or inputs being wrapped.

  Props:
      className: string
                 Added to the className of wrapping container using classNames utility
                 ex: className='username'

      label,     string | ReactClass | { component: ReactClass, ...props}
      error:     Content for the label and error portions of the field.
                 ex: error='Invalid Username'
                     label={ component: MyErrorComponent, someProp: 'somepropvalue' }

      <other>:   Most other props are spread onto the .form-field containing element,
                 so you can do things like attach event listeners directly to the <Field>
                 element and it should work as expected.

  Example:
      <Field className='username required'
             label={ component: MyLabelComponent, someProp: 'somepropvalue'}
             error='Invalid: Required'>
        <input />
      </Field>

      <Field label='Remember Me'>
        <input type='checkbox' />
      </Field>


*/

import React, { useState } from 'react';
import classNames from 'classnames/bind';
import SelectInput from 'core/ui/forms/select-input';
import stringOrComponent from 'core/utils/string-or-component';

let last_id = 1;
const new_id = function() {
  last_id++;
  return `item__${last_id}`;
};

export default function Field({ className, label, error, onFocus, onBlur, children, ...rest }) {
  const [focused, setFocused] = useState(false);

  const handleFocus = (ev) => {
    setFocused(true);
    return onFocus && onFocus(ev);
  };

  const handleBlur = (ev) => {
    setFocused(false);
    return onBlur && onBlur(ev);
  };

  let first_id = null;
  let isFileType = false, isRadioType = false, isCheckboxType = false, isTextarea = false, isSelect = false;

  const child_nodes = React.Children.map(children, (item) => {
    if (!React.isValidElement(item)) return item;
    if (['input', 'select', 'textarea'].includes(item.type) || item.type === SelectInput) {
      const id = new_id();
      if (!first_id) { first_id = id; }
      const el = React.cloneElement(item, {id});
      isFileType = isFileType || ((el.props != null ? el.props.type : undefined) === 'file');
      isRadioType = isRadioType || ((el.props != null ? el.props.type : undefined) === 'radio');
      isCheckboxType = isCheckboxType || ((el.props != null ? el.props.type : undefined) === 'checkbox');
      isTextarea = isTextarea || (el.type === 'textarea');
      isSelect = isSelect || (el.type === 'select');
      return el;
    }
    return item;
  });

  const classNamesFromProps = (className || '').split(' ');
  const fieldClass = classNames('form-field', classNamesFromProps, {
    'has-error': error,
    'field-type-radio': isRadioType,
    'field-type-checkbox': isCheckboxType,
    'field-type-file': isFileType,
    'field-type-textarea': isTextarea,
    'field-type-select': isSelect,
    'has-focus': focused,
  });

  return <div className={fieldClass} onFocus={handleFocus} onBlur={handleBlur} {...rest}>
    { (isRadioType || isCheckboxType) && child_nodes }
    { label &&
        <label htmlFor={first_id} className={classNames({'button': isFileType})}>
            <span className='label-text'>{ stringOrComponent(label) }</span>
        </label>
    }
    { !(isRadioType || isCheckboxType) && child_nodes }
    { error &&
        <span className='form-field-error'>{stringOrComponent(error)}</span>
    }
  </div>;
}

